// ** React Imports
import { useEffect } from 'react'

// ** Other Imports
import Cookies from "js-cookie"
import CryptoJS from "crypto-js"
import { jwt_secret_key, HOMEURL, baseURL } from "./jwtService"
import axios from "axios"
import { dashboardURL } from "../../views/Validator"

// --- Top-level definitions for export ---
// Create URLSearchParams once when the module is loaded.
const params = new URLSearchParams(window.location.search)
console.log("Module loaded. URLSearchParams created:", params.toString())

// Define and export 'home' so it can be imported elsewhere.
export const home = params.get("home") || ""
console.log("Module loaded. Exported 'home' parameter from URL:", home)
// ------------------------------------------

const CheckLogin = () => {
    console.log("CheckLogin component is rendering.")

    useEffect(() => {
        console.log("useEffect hook is triggered.")

        const performCheck = async () => {
            console.log("performCheck async function started.")

            // 'params' is already defined at the top level, but we can re-read it
            // inside the effect if the URL could change without a full page reload (less common).
            // For this use case, using the top-level 'params' is fine.
            const params_app = params.get("app") || ""
            console.log("Parameter 'app' from URL:", params_app)

            const rawXut = params.get("xut") || ""
            console.log("Raw parameter 'xut' from URL:", rawXut)

            // The 'home' constant is now imported from the module scope.
            if (home) {
                console.log("'home' parameter found. Setting 'home' in localStorage:", home)
                localStorage.setItem("home", home)
            } else {
                console.log("'home' parameter not found.")
            }

            const xut = decodeURIComponent(rawXut)
            console.log("Decoded 'xut' parameter:", xut)
            
            if (!xut) {
                console.log("'xut' is missing. Redirecting to merchant apps page.")
                window.location.href = `${HOMEURL}/merchant/bundles/`
                return // Stop execution
            }

            console.log("Checking for 'isLoad' in sessionStorage.")
            if (sessionStorage.getItem("isLoad")) {
                console.log("'isLoad' found in sessionStorage. Aborting check to prevent re-execution.")
                return // Stop execution
            }
            console.log("'isLoad' not found. Setting 'isLoad' in sessionStorage to 'true'.")
            sessionStorage.setItem("isLoad", "true")

            let decryptedToken
            try {
                console.log("Attempting to decrypt 'xut' token.")
                const bytes = CryptoJS.AES.decrypt(xut, jwt_secret_key)
                decryptedToken = bytes.toString(CryptoJS.enc.Utf8)
                console.log("Decryption successful.")

                if (!decryptedToken) {
                    console.error("Decryption resulted in an empty token.")
                    throw new Error("Decryption failed to produce a token.")
                }
                console.log("Decrypted token is not empty.")

            } catch (error) {
                console.error("An error occurred during token decryption:", error)
                console.log("Redirecting to merchant apps page due to decryption error.")
                window.location.href = `${HOMEURL}/merchant/bundles/`
                sessionStorage.removeItem('isLoad')
                return // Stop execution
            }

            try {
                console.log("Main logic block started: setting cookie, fetching user data.")
                console.log("Setting 'xircls_user_token' cookie.")
                Cookies.set("xircls_user_token", decryptedToken, {
                    path: "/",
                    sameSite: "None",
                    secure: true
                })
                
                console.log("Parsing the decrypted token string into a JSON object.")
                const xircls_user_token = JSON.parse(decryptedToken)
                console.log("Parsed token object:", xircls_user_token)

                const token = xircls_user_token?.access
                console.log("Extracted access token for API call:", token)
                
                const apiURL = `${baseURL}/merchant/get_user_data/`
                console.log(`Making axios GET request to: ${apiURL}`)
                
                const response = await axios.get(apiURL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    timeout: 10000
                })

                console.log("API call successful. Response data:", response.data)

                const updatedPermission = {
                    appName: params_app,
                    multipleDomain: response?.data?.outlet_list,
                    apiKey: response?.data?.outlet_list?.[0]?.api_key,
                    web_url: response?.data?.outlet_list?.[0]?.web_url,
                    installedApps: response?.data?.installed_apps,
                    campaign: response?.data?.status,
                    permissionList: response?.data?.permission_list,
                    super_user: response?.data?.super_user,
                    is_super_user: response?.data?.is_super_user,
                    multi_user_key: response?.data?.multi_user_key,
                    logged_in_user: response?.data?.logged_in_user,
                    user_settings: response?.data?.user_settings
                }
                console.log("Constructed user permission object:", updatedPermission)

                console.log("Setting 'userPermission' in localStorage.")
                localStorage.setItem("userPermission", JSON.stringify(updatedPermission || {}))
                
                if (params_app) {
                    console.log(`'params_app' (${params_app}) exists. Determining redirect path.`)
                    const isFullPath = params_app.includes('/')
                    console.log(`Is '${params_app}' a full path?`, isFullPath)

                    if (isFullPath) {
                        const redirectURL = `${dashboardURL?.user_profiles}/${params_app}`
                        console.log(`Redirecting to full path: ${redirectURL}`)
                        window.location.href = redirectURL
                    } else if (dashboardURL[params_app]) {
                        console.log(`Found a matching key for '${params_app}' in dashboardURL.`)
                        const page = params_app === 'infiniti' ? 'merchant/dashboard' : params_app === 'crm' ? 'merchant/customers' : params_app === 'chatbot' ? 'merchant/chatbot/reports' : `merchant/${params_app}`
                        const redirectURL = `${dashboardURL[params_app]}/${page}`
                        console.log(`Calculated page as '${page}'. Redirecting to: ${redirectURL}`)
                        window.location.href = redirectURL
                    } else {
                        const redirectURL = `${HOMEURL}/merchant/bundles/`
                        console.log(`No specific redirect rule for '${params_app}'. Redirecting to default: ${redirectURL}`)
                        window.location.href = redirectURL
                    }
                } else {
                    const redirectURL = `${HOMEURL}/merchant/bundles/`
                    console.log("'params_app' is missing. Redirecting to default merchant apps page:", redirectURL)
                    window.location.href = redirectURL
                }
                
                console.log("Process successful. Removing 'isLoad' from sessionStorage.")
                sessionStorage.removeItem('isLoad')

            } catch (err) {
                console.error("An error occurred during the API call or subsequent processing:", err)
                console.log("Cleaning up cookies and localStorage due to error.")
                Cookies.remove('xircls_user_token')
                localStorage.removeItem('userPermission')
                sessionStorage.removeItem('isLoad')
                console.log("Redirecting to merchant apps page after catching error.")
                window.location.href = `${HOMEURL}/merchant/bundles/`
            }
        }

        performCheck()
    }, []) // Empty dependency array ensures this runs only once.

    console.log("CheckLogin component is returning null.")
    return null 
}

export default CheckLogin


// import Cookies from "js-cookie"
// import CryptoJS from "crypto-js"
// import { jwt_secret_key, HOMEURL, omcAPI, baseURL } from "./jwtService"
// import axios from "axios"
// import { dashboardURL } from "../../views/Validator"

// const params = new URLSearchParams(window.location.search)
// export const home = params.get("home") || ""
// if (home) {
//     localStorage.setItem("home", home)
//   }
// const CheckLogin = async () => {
//     // const params = new URLSearchParams(window.location.search)
//     const params_app = params.get("app") || ""
//     const rawXut = params.get("xut") || ""
//     // const home = params.get("home") || ""
//     const xut = decodeURIComponent(rawXut)
//     console.log()
//     if (!xut) {
//         window.location.href = `${HOMEURL}/merchant/bundles/`
//         console.log("xut not found")
//         return
//     }

//     if (sessionStorage.getItem("isLoad")) {
//         return
//     }
// console.log("homeURL", home)
//     sessionStorage.setItem("isLoad", "true")

//     const bytes = CryptoJS.AES.decrypt(xut, jwt_secret_key)
//     const decryptedToken = bytes.toString(CryptoJS.enc.Utf8)

//     let xircls_user_token
//     try {
//         Cookies.set("xircls_user_token", decryptedToken, {
//             path: "/",
//             sameSite: "None",
//             secure: true
//         })

//         xircls_user_token = JSON.parse(decryptedToken)
//     } catch (error) {
//         console.error("Error parsing xut token:", error)
//         window.location.href = `${HOMEURL}/merchant/bundles/`
//         sessionStorage.removeItem('isLoad')
//         return
//     }

//     try {
//         const token = xircls_user_token?.access

//         // Determine which API base URL and endpoint to use
//         const apiToUse = params_app === "oh_my_customer" ? omcAPI : baseURL
//         const endpoint = params_app === "oh_my_customer" ? "/utility/get_user_data/" : "/merchant/get_user_data/"

//         const response = await axios.get(`${apiToUse}${endpoint}`, {
//             headers: {
//                 Authorization: `Bearer ${token}`,
//                 "Content-Type": "application/json"
//             },
//             timeout: 10000
//         })

//         const updatedPermission = {
//             appName: params_app,
//             multipleDomain: response?.data?.outlet_list,
//             apiKey: response?.data?.outlet_list?.[0]?.api_key,
//             web_url: response?.data?.outlet_list?.[0]?.web_url,
//             installedApps: response?.data?.installed_apps,
//             campaign: response?.data?.status,
//             permissionList: response?.data?.permission_list,
//             super_user: response?.data?.super_user,
//             is_super_user: response?.data?.is_super_user,
//             multi_user_key: response?.data?.multi_user_key,
//             logged_in_user: response?.data?.logged_in_user,
//             user_settings: response?.data?.user_settings
//         }

//         localStorage.setItem("userPermission", JSON.stringify(updatedPermission || {}))

//         if (params_app) {
//             const isFullPath = params_app.includes('/')

//             if (isFullPath && baseURL) {
//                 window.location.href = `${dashboardURL?.user_profiles}/${params_app}`
//             } else if (dashboardURL[params_app]) {
//                 const page = params_app === 'infiniti' ? 'merchant/dashboard' : params_app === 'crm' ? 'merchant/customers' : params_app === 'chatbot' ? 'merchant/chatbot/reports' : `merchant/${params_app}`
//                 window.location.href = `${dashboardURL[params_app]}/${page}`
//             } else {
//                 window.location.href = `${HOMEURL}/merchant/bundles/`
//             }
//         }

//         sessionStorage.removeItem('isLoad')

//     } catch (err) {
//         Cookies.remove('xircls_user_token')
//         localStorage.removeItem('userPermission')
//         sessionStorage.removeItem('userPermission')
//         window.location.href = `${HOMEURL}/merchant/bundles/`
//         console.error("Error in API call:", err)
//         sessionStorage.removeItem('isLoad')
//     }
// }

// export default CheckLogin